/**
 * Saves a fetched health-record file's bytes into the device's real,
 * public Downloads folder (not this app's own private storage) via
 * `cordova-plugin-saf-mediastore`.
 *
 * A plain `cordova-plugin-file` write (this module's first version)
 * could only reach this app's own external-data directory —
 * confirmed working on a real device (2026-09-18), but reported back
 * as "buried deep and hard to find". Writing into the real shared
 * Downloads folder needs Android's Storage Access Framework/MediaStore
 * APIs on modern Android: `cordova-plugin-file`'s own README states
 * plainly that shared directories like Downloads are "no longer
 * writable" via its plain file APIs as of API 30, with no
 * `WRITE_EXTERNAL_STORAGE`/`requestLegacyExternalStorage` workaround
 * documented — confirmed directly from that plugin's README before
 * switching to this one.
 *
 * `cordova-plugin-saf-mediastore` (github.com/customautosys/cordova-
 * plugin-saf-mediastore) is a small, single-maintainer package that
 * turned out to need several real fixes to even build (see the yarn
 * patch removing its unscoped storage permissions and adding its
 * missing/mis-versioned androidx.documentfile dependency) and has at
 * least one more confirmed bug this file works around rather than
 * patches, documented below.
 *
 * IMPORTANT — `folder` semantics, confirmed by reading the plugin's own
 * Java source (`writeFile`): passing ANY non-empty string as `folder`
 * (e.g. the literal "Downloads") makes it call
 * `DocumentFile.fromTreeUri(context, Uri.parse(folder))` — i.e. it
 * expects a real SAF tree URI obtained via the plugin's own
 * `selectFolder()` picker, NOT a folder name. Its README's "the
 * default folder is the Downloads folder" refers to what happens when
 * `folder` is omitted entirely (the plugin's own `else` branch, a
 * clean `ContentResolver.insert` into `MediaStore.Downloads` — no
 * permission needed). Passing the string "Downloads" as this module
 * originally did sent it down the tree-URI branch instead, which threw
 * (confirmed live, 2026-09-19: `Uri.parse("Downloads")` produces
 * something `DocumentFile.fromTreeUri` can't resolve, and the
 * subsequent `documentFile.listFiles()` call fails) — surfaced to this
 * app only as an opaque empty-string error, because the plugin's own
 * `debugLog` helper calls `WebView.evaluateJavascript` to log the real
 * exception and silently swallows ITS OWN failure when that throws
 * (plugin code runs off the UI thread), discarding the real message in
 * the process. So: never pass `folder` to `writeFile` here.
 */

interface SafMediastorePlugin {
  writeFile(params: { data: string; filename: string }): Promise<string>;
}

function safMediastore(): SafMediastorePlugin {
  const plugin = (window as any).cordova?.plugins?.safMediastore;
  if (!plugin) {
    throw new Error(
      'cordova-plugin-saf-mediastore is required to download a file — not available in the browser dev build.',
    );
  }
  return plugin;
}

// A record's filename comes from whatever the mobile client sent at
// upload time (ultimately from the OS file picker) — sanitize before
// using it as a filename regardless, since a path separator here could
// otherwise be read as a subfolder path by the plugin.
function sanitizeFileName(name: string): string {
  return name.replace(/[\\/]/g, '_') || 'download';
}

// btoa() only accepts a "binary string" (one char per byte), and
// spreading a large Uint8Array directly into String.fromCharCode can
// overflow the call stack — chunking avoids both.
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

export interface SavedFile {
  uri: string;
  filename: string;
}

/** Writes `data` into the device's real Downloads folder (via
 * MediaStore — see this file's module docs for why `folder` must NOT
 * be passed) under the given filename, and resolves with that same
 * filename (kept as part of the return shape, alongside the URI, so
 * callers don't need a separate lookup for what to show the user).
 *
 * Does NOT do its own collision-avoidance: `getUri` (which a previous
 * version of this function used to check for an existing file first)
 * has a confirmed bug in this plugin version — its Java code reads
 * `params.getString("folder")` but never assigns the result, so
 * `folder` is always null internally regardless of what's passed,
 * making any folder-scoped lookup fail unconditionally. Whether a
 * same-named second download overwrites the first or is renamed is
 * therefore up to Android's own MediaStore insert behavior, not this
 * app's — VERIFIED on a real device (2026-09-19): MediaStore renames
 * it (appending "(1)", "(2)", ...) on its own, so no app-side
 * collision-avoidance is needed here after all. */
export async function saveRecordContentToDevice(fileName: string, data: ArrayBuffer): Promise<SavedFile> {
  const plugin = safMediastore();
  const safeName = sanitizeFileName(fileName);
  const uri = await plugin.writeFile({ data: arrayBufferToBase64(data), filename: safeName });
  return { uri, filename: safeName };
}
